import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useDashboardData } from "@/hooks/useDashboardData";
import { StatsGrid } from "@/components/dashboard/StatsGrid";
import { MyListingsTable } from "@/components/dashboard/MyListingsTable";
import { PromoteModal } from "@/components/dashboard/PromoteModal";
import { BumpModal } from "@/components/dashboard/BumpModal";
import { AccountSecurity } from "@/components/dashboard/AccountSecurity";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { listings, stats, remove, repost } = useDashboardData(user);
  const [promoteId, setPromoteId] = useState(null);
  const [bumpId, setBumpId] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    await remove(id);
    toast.success("Listing deleted");
  };

  const handleRepost = async (id) => {
    try {
      await repost(id);
      toast.success("Ad reposted — live for another 30 days");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not repost");
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 mt-8" data-testid="dashboard-page">
      <h1 className="font-display text-3xl md:text-5xl uppercase border-b-2 border-black pb-2">/ Dashboard</h1>
      <div className="font-mono-data text-xs uppercase mt-1 text-gray-600">Welcome back, {user.name || user.email}</div>

      <StatsGrid stats={stats} />

      <div className="flex items-center justify-between mt-10 border-b-2 border-black pb-2">
        <h2 className="font-display text-2xl uppercase">/ My Listings</h2>
        <Link to="/post" data-testid="dashboard-new-listing-btn" className="bg-[#0000EE] text-white px-4 py-2 border-2 border-[#0000EE] font-mono-data text-xs uppercase hover:bg-[#0000AA] hover:border-[#0000AA] no-underline hover:no-underline flex items-center gap-1"><Plus size={14} /> New Ad</Link>
      </div>

      <div className="font-mono-data text-xs uppercase text-gray-600 mt-3" data-testid="repost-rule-note">Ads stay live for 30 days. Repost before they expire to keep them up — expired ads are hidden, then deleted after 30 more days.</div>
      <MyListingsTable listings={listings} onPromote={setPromoteId} onDelete={handleDelete} onRepost={handleRepost} onBump={setBumpId} />

      <AccountSecurity user={user} />

      {bumpId && <BumpModal listingId={bumpId} onClose={() => setBumpId(null)} />}
      {promoteId && <PromoteModal listingId={promoteId} onClose={() => setPromoteId(null)} />}
    </div>
  );
}
